import React, { createContext, useContext, ReactNode } from 'react';
import { useAnalytics } from '@/hooks/useAnalytics';
import { resetFeatureTracking } from '@/hooks/useFeatureTracking';
import { resetPlanLimitTracking } from '@/components/common/PlanLimitBanner';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';
import { useAuth, Organization, OrganizationMember } from '@/hooks/useAuth';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { Database } from '@/integrations/supabase/types';
import type { Consumer } from '@/services/consumers.service';

type Profile = Database['public']['Tables']['profiles']['Row'];
type AppRole = Database['public']['Enums']['app_role'];
type OrgRole = 'owner' | 'admin' | 'atendente' | 'prestador';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  role: AppRole | null;
  orgRole: OrgRole | null;
  organizationId: string | null;
  isLoading: boolean;
  isAdmin: boolean;
  isOwner: boolean;
  isAtendente: boolean;
  isPrestador: boolean;
  organization: Organization | null;
  membership: OrganizationMember | null;
  needsOnboarding: boolean;
  accountDeactivated: boolean;
  consumer: Consumer | null;
  isConsumer: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, nome: string) => Promise<{ error: AuthError | null; needsEmailConfirmation?: boolean }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  refreshOrganization: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshConsumer: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { identify, group, reset } = useAnalytics();
  const previousUserId = React.useRef<string | null>(null);

  React.useEffect(() => {
    const currentUserId = auth.user?.id ?? null;

    // Logout transition: previously had user, now none
    if (previousUserId.current && !currentUserId) {
      reset();
      // Also clear the per-session feature_used dedup Set
      resetFeatureTracking();
      resetPlanLimitTracking();
      clearSentryUser();
      previousUserId.current = null;
      return;
    }

    // Track the current user id as soon as we have one, regardless of org state.
    // This ensures logout detection works even if the user signs out mid-onboarding
    // (before their organization is hydrated), and that onboarding events are
    // attributed to the signed-in user rather than an anonymous distinct_id.
    if (currentUserId) {
      previousUserId.current = currentUserId;

      // Identify the user even before org loads (onboarding flow)
      identify(currentUserId, {
        email: auth.user?.email,
        name: auth.profile?.nome ?? undefined,
        role: auth.role ?? undefined,
        organization_id: auth.organization?.id,
        plan: auth.organization?.plano,
        subscription_status: auth.organization?.subscription_status,
        created_at: auth.user?.created_at,
      });

      setSentryUser(
        { id: currentUserId, email: auth.user?.email },
        {
          organization_id: auth.organization?.id ?? '',
          plan: auth.organization?.plano ?? '',
          subscription_status: auth.organization?.subscription_status ?? '',
        },
      );

      // Group only when org is ready
      if (auth.organization) {
        group(auth.organization.id, {
          plan: auth.organization.plano,
          subscription_status: auth.organization.subscription_status,
        });
      }
    }
  }, [auth.user, auth.profile, auth.role, auth.organization, identify, group, reset]);

  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext deve ser usado dentro de um AuthProvider');
  }
  return context;
}
